import {
  Badge,
  Box,
  Checkbox,
  Flex,
  Heading,
  Image,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import {
  ALL_PARTNER_APPS,
  OwnApp,
  PartnerGame,
  cellKey,
} from "views/admin/appTargeting/types";

interface AppTargetingMatrixProps {
  platformId: number;
  platformName: string;
  games: PartnerGame[];
  ownApps: OwnApp[];
  hidden: Set<string>;
  initialHidden: Set<string>;
  onToggle: (partnerAppId: string, ownAppId: string) => void;
}

const stickyColumnProps = {
  position: "sticky" as const,
  left: 0,
  zIndex: 1,
  bg: "white",
};

const AppTargetingMatrix = ({
  platformId,
  platformName,
  games,
  ownApps,
  hidden,
  initialHidden,
  onToggle,
}: AppTargetingMatrixProps) => {
  const isHidden = (partnerAppId: string, ownAppId: string) =>
    hidden.has(cellKey(platformId, partnerAppId, ownAppId));
  const isChanged = (partnerAppId: string, ownAppId: string) =>
    hidden.has(cellKey(platformId, partnerAppId, ownAppId)) !==
    initialHidden.has(cellKey(platformId, partnerAppId, ownAppId));

  const hiddenCount = ownApps.reduce(
    (count, app) =>
      count +
      (isHidden(ALL_PARTNER_APPS, app.appId) ? 1 : 0) +
      games.filter((game) => isHidden(game.appId, app.appId)).length,
    0
  );

  return (
    <Card p={4} mb={5}>
      <Flex align="center" gap={3} mb={3}>
        <Heading size="md">{platformName}</Heading>
        <Badge colorScheme={platformId === 1 ? "green" : "gray"} variant="solid">
          {games.length} game{games.length === 1 ? "" : "s"}
        </Badge>
        {hiddenCount > 0 && (
          <Badge colorScheme="red" variant="subtle">
            {hiddenCount} hidden
          </Badge>
        )}
      </Flex>

      {ownApps.length === 0 ? (
        <Text color="gray.500" fontSize="sm">
          We have no {platformName} app.
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table size="sm" variant="simple" w="100%" style={{ tableLayout: "fixed" }}>
            <Thead>
              <Tr>
                <Th {...stickyColumnProps} zIndex={3} w="200px" px={2}>
                  Game
                </Th>
                {ownApps.map((app) => (
                  <Th
                    key={app.appId}
                    textAlign="center"
                    px={1}
                    py={2}
                    whiteSpace="normal"
                    verticalAlign="bottom"
                    lineHeight="1.2"
                  >
                    <Tooltip label={app.appId} openDelay={400}>
                      <Text as="span" fontSize="xs">
                        {app.name}
                      </Text>
                    </Tooltip>
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              <Tr bg="gray.50">
                <Td {...stickyColumnProps} bg="gray.50" px={2} py={1}>
                  <Text fontWeight="bold" fontSize="sm">
                    All games
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    Every {platformName} campaign
                  </Text>
                </Td>
                {ownApps.map((app) => (
                  <Td key={app.appId} textAlign="center" px={1} py={1}>
                    <Tooltip
                      label={
                        isHidden(ALL_PARTNER_APPS, app.appId)
                          ? `Hidden in ${app.name} for every game`
                          : `Shown in ${app.name}`
                      }
                      openDelay={400}
                    >
                      <Box display="inline-block">
                        <Checkbox
                          size="md"
                          colorScheme={
                            isChanged(ALL_PARTNER_APPS, app.appId)
                              ? "orange"
                              : "blue"
                          }
                          isChecked={!isHidden(ALL_PARTNER_APPS, app.appId)}
                          onChange={() => onToggle(ALL_PARTNER_APPS, app.appId)}
                        />
                      </Box>
                    </Tooltip>
                  </Td>
                ))}
              </Tr>

              {games.length === 0 && (
                <Tr>
                  <Td colSpan={ownApps.length + 1}>
                    <Text color="gray.500" fontSize="sm">
                      This partner has no {platformName} game yet. The All games
                      row still applies to games added later.
                    </Text>
                  </Td>
                </Tr>
              )}

              {games.map((game) => (
                <Tr key={game.appId} _hover={{ bg: "blue.50" }}>
                  <Td {...stickyColumnProps} px={2} py={1}>
                    <Flex align="center" gap={2} overflow="hidden">
                      {game.iconUrl && (
                        <Image
                          src={game.iconUrl}
                          alt=""
                          boxSize="24px"
                          borderRadius="6px"
                          flexShrink={0}
                          fallback={<Box boxSize="24px" flexShrink={0} />}
                        />
                      )}
                      <Box overflow="hidden">
                        <Text fontWeight="medium" fontSize="sm" isTruncated>
                          {game.title}
                        </Text>
                        <Text
                          fontFamily="mono"
                          fontSize="xs"
                          color="gray.500"
                          isTruncated
                        >
                          {game.appId}
                        </Text>
                      </Box>
                    </Flex>
                  </Td>
                  {ownApps.map((app) => {
                    const inheritedHidden = isHidden(
                      ALL_PARTNER_APPS,
                      app.appId
                    );
                    const ownHidden = isHidden(game.appId, app.appId);

                    return (
                      <Td key={app.appId} textAlign="center" px={1} py={1}>
                        <Tooltip
                          label={
                            inheritedHidden
                              ? `Hidden by the All games rule${
                                  ownHidden ? " (also hidden on its own)" : ""
                                }`
                              : ownHidden
                              ? `Hidden in ${app.name}`
                              : `Shown in ${app.name}`
                          }
                          openDelay={400}
                        >
                          <Box display="inline-block">
                            <Checkbox
                              size="md"
                              colorScheme={
                                isChanged(game.appId, app.appId)
                                  ? "orange"
                                  : "blue"
                              }
                              isChecked={!inheritedHidden && !ownHidden}
                              isDisabled={inheritedHidden}
                              onChange={() => onToggle(game.appId, app.appId)}
                            />
                          </Box>
                        </Tooltip>
                      </Td>
                    );
                  })}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Card>
  );
};

export default AppTargetingMatrix;
